import { frontendApi as api } from './base-api';
const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    postV1Contact: build.mutation<
      PostV1ContactApiResponse,
      PostV1ContactApiArg
    >({
      query: (queryArg) => ({
        url: `/v1/contact`,
        method: 'POST',
        body: queryArg.contactMessage,
      }),
    }),
    postV1TrackingEvents: build.mutation<
      PostV1TrackingEventsApiResponse,
      PostV1TrackingEventsApiArg
    >({
      query: (queryArg) => ({
        url: `/v1/tracking/events`,
        method: 'POST',
        body: queryArg.body,
      }),
    }),
    getV1UsersMe: build.query<GetV1UsersMeApiResponse, GetV1UsersMeApiArg>({
      query: () => ({ url: `/v1/users/me` }),
    }),
    getV1UsersByEmail: build.query<
      GetV1UsersByEmailApiResponse,
      GetV1UsersByEmailApiArg
    >({
      query: (queryArg) => ({ url: `/v1/users/${queryArg.email}` }),
    }),
    getV1Locations: build.query<
      GetV1LocationsApiResponse,
      GetV1LocationsApiArg
    >({
      query: (queryArg) => ({
        url: `/v1/locations`,
        params: {
          country: queryArg.country,
        },
      }),
    }),
    postV1Feedback: build.mutation<
      PostV1FeedbackApiResponse,
      PostV1FeedbackApiArg
    >({
      query: (queryArg) => ({
        url: `/v1/feedback`,
        method: 'POST',
        body: queryArg.body,
      }),
    }),
    getV1Health: build.query<GetV1HealthApiResponse, GetV1HealthApiArg>({
      query: () => ({ url: `/v1/health` }),
    }),
    getV1Config: build.query<GetV1ConfigApiResponse, GetV1ConfigApiArg>({
      query: () => ({ url: `/v1/config` }),
    }),
  }),
  overrideExisting: false,
});
export { injectedRtkApi as api };
export type PostV1ContactApiResponse =
  /** status 201 Contact message sent. */ string;
export type PostV1ContactApiArg = {
  contactMessage: ContactMessage;
};
export type PostV1TrackingEventsApiResponse =
  /** status 200 Logged event. */ string;
export type PostV1TrackingEventsApiArg = {
  body: {
    /** Event name:
     * `PAGE_VIEW` - User visits view
     * `CONTENT_VIEW` - User visits a view section
     * `USER_ACTION` - User performs an action
     */
    event: 'PAGE_VIEW' | 'CONTENT_VIEW' | 'USER_ACTION';
    /** User's session uniq identifier */
    sessionId: string;
    /** Event metadata */
    data: object;
  };
};
export type GetV1UsersMeApiResponse =
  /** status 200 Profile data fetched. */ User;
export type GetV1UsersMeApiArg = void;
export type GetV1UsersByEmailApiResponse =
  /** status 200 Target email profile data fetched. */ User;
export type GetV1UsersByEmailApiArg = {
  /** Target email. */
  email: string;
};
export type GetV1LocationsApiResponse =
  /** status 200 Locations fetched. */ CdsLocation[];
export type GetV1LocationsApiArg = {
  /** Country code to filter locations by. If not provided, all locations will be returned. */
  country?: string;
};
export type PostV1FeedbackApiResponse = /** status 201 Feedback sent. */ string;
export type PostV1FeedbackApiArg = {
  body: {
    /** The user rating score. */
    score: number;
    /** The maximum user rating score. */
    maxScore: number;
  };
};
export type GetV1HealthApiResponse = unknown;
export type GetV1HealthApiArg = void;
export type GetV1ConfigApiResponse = /** status 200 Configuration fetched. */ {
  /** Maintenance status of the application */
  maintenanceStatus?: {
    /** Is the maintenance status message enabled */
    enabled?: boolean;
    /** Message to be displayed */
    text?: string;
    /** Can the banner be dismissed by users */
    dismissable?: boolean;
    /** Is the maintenance status an emergency */
    emergency?: boolean;
    /** Is the force viewer mode enabled */
    forceViewerMode?: boolean;
  };
};
export type GetV1ConfigApiArg = void;
export type OpenApiValidatorError = {
  /** Error location in OpenAPI specifications. */
  path: string;
  /** Error message for the specific validation. */
  message: string;
  /** OpenAPI validator errorCode which defines the rule this error is based upon. */
  errorCode: string;
};
export type OpenApiValidatorCompleteError = {
  /** Status code of error response */
  status: string;
  /** Overarching error message for validation. */
  message: string;
  errors: OpenApiValidatorError[];
};
export type Unauthorized = string;
export type InternalServerError = {
  /** Internal server error. */
  error: string;
};
export type ContactMessage = {
  /** Message content to write to product owner. */
  message: string;
  /** E-Mail for response to requestor. */
  email: string;
};
export type User = {
  /** User's office name. */
  cdsLocation: string;
  /** User's company name. */
  companyName: string;
  /** User's country. */
  country: string;
  /** User's display name. */
  displayName: string;
  /** Username. */
  username: string;
  /** User's first name. */
  givenName: string;
  /** User's last name. */
  surname: string;
  fullName: string;
  /** User's company role. */
  jobTitle: string;
  /** User's company department. */
  department?: string;
  /** User's manager email. */
  manager?: string;
};
export type CdsLocation = {
  /** Location unique identifier. */
  id: string;
  /** Location display name. */
  name: string;
  /** Country code in lowercase. */
  country: string;
};
export const {
  usePostV1ContactMutation,
  usePostV1TrackingEventsMutation,
  useGetV1UsersMeQuery,
  useGetV1UsersByEmailQuery,
  useGetV1LocationsQuery,
  usePostV1FeedbackMutation,
  useGetV1HealthQuery,
  useGetV1ConfigQuery,
} = injectedRtkApi;
